import React from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Code, Layout, Server, Brain, Database, Cloud, Shield } from 'lucide-react';
import { skills } from '../data/mock';
import { SECTIONS, createSectionVariants } from '../lib/motionDirector';

const iconMap = {
  Code: Code,
  Layout: Layout,
  Server: Server,
  Brain: Brain,
  Database: Database,
  Cloud: Cloud,
  Shield: Shield,
};

const kanjiMap = {
  'Programming Arts': '編',
  'Frontend Mastery': '表',
  'Backend Fortress': '城',
  'AI/ML Wisdom': '知',
  'Data Sanctuaries': '庫',
  'DevOps & Cloud': '雲',
  'Security Guard': '盾',
};

const Skills = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const skillCategories = Object.values(skills);

  // Parallax scroll effect for shuriken
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  // Shuriken moves horizontally from left to right when section is 20% visible
  const shurikenX = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 0, 800, 800]);
  const shurikenRotate = useTransform(scrollYProgress, [0, 1], [0, 720]);
  const shurikenScale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.1, 1]);

  // Motion Director - Static section for attention reset
  const variants = createSectionVariants(SECTIONS.skills);

  return (
    <section id="skills" className="relative py-32 bg-zinc-950 overflow-hidden" ref={ref}>
      {/* Enhanced cream-gold hue overlay - lighter */}
      <div className="absolute inset-0 bg-gradient-to-bl from-amber-900/18 via-yellow-950/8 to-amber-800/12" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(251,191,36,0.08),transparent_50%)]" />
      
      {/* Shuriken backdrop - left side with parallax */}
      <motion.img 
        src="/suriken.png" 
        alt="" 
        className="absolute left-10 top-2/3 -translate-y-1/2 w-[300px] opacity-20 pointer-events-none select-none drop-shadow-[0_0_30px_rgba(200,200,200,0.3)]"
        style={{
          x: shurikenX,
          rotate: shurikenRotate,
          scale: shurikenScale,
        }}
      />
      
      {/* Kanji Typography Layer - Large watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-[25rem] font-bold text-amber-100/[0.04] select-none tracking-wider">技術</div>
      </div>

      {/* Japanese background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 text-[18rem] font-bold text-amber-100/[0.02] select-none drop-shadow-[0_0_40px_rgba(220,38,38,0.25)]">技</div>
        <div className="absolute bottom-20 right-20 text-[12rem] font-bold text-red-900/[0.03] select-none drop-shadow-[0_0_60px_rgba(220,38,38,0.3)]">術</div>
        
        {/* Geometric patterns */}
        <svg className="absolute inset-0 w-full h-full opacity-5" viewBox="0 0 100 100" preserveAspectRatio="none">
          <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#D4A574" strokeWidth="0.5" />
          </pattern>
          <rect width="100" height="100" fill="url(#grid)" />
        </svg>
      </div>

      <motion.div 
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
        variants={variants.container}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {/* Section Header */}
        <motion.div
          variants={variants.item}
          className="text-center mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-700" />
            <span className="text-4xl text-amber-800/30">技</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-700" />
          </div>
          <span className="inline-block px-4 py-1 text-xs tracking-[0.3em] text-amber-500/80 border border-amber-900/40 rounded-full mb-4">
            ARSENAL
          </span>
          <h2 className="font-bebas text-5xl md:text-7xl tracking-wider text-amber-50">
            TECHNIQUES MASTERED
          </h2>
        </motion.div>

        {/* Skills Bento Box Grid - Optimized Layout */}
        <div className="grid grid-cols-12 auto-rows-[160px]">
          {skillCategories.map((category, index) => {
            const IconComponent = iconMap[category.icon];
            const kanji = kanjiMap[category.title] || '技';
            
            // Optimized bento box layout matching the design
            const bentoSizes = [
              'col-span-12 md:col-span-6 lg:col-span-4 row-span-2', // 0: Programming Arts - Medium tall
              'col-span-12 md:col-span-6 lg:col-span-4 row-span-2', // 1: Frontend Mastery - Medium tall  
              'col-span-12 md:col-span-6 lg:col-span-4 row-span-2', // 2: Backend Fortress - Small
              'col-span-12 md:col-span-12 lg:col-span-12 row-span-2', // 3: AI/ML Wisdom - Large featured
              'col-span-12 md:col-span-6 lg:col-span-4 row-span-1', // 4: Data Sanctuaries - Small
              'col-span-12 md:col-span-6 lg:col-span-4 row-span-1', // 5: DevOps & Cloud - Small
              'col-span-12 md:col-span-6 lg:col-span-4 row-span-1', // 6: Security Guard - Small
            ];
            
            const rowSpan = bentoSizes[index].match(/row-span-(\d+)/)?.[1] || 1;
            const isFeatured = index === 3; // AI/ML is featured
            const isLarge = rowSpan >= 2;
            const showDescription = isLarge;
            const maxSkills = isFeatured ? 12 : (isLarge ? 6 : 3);
            
            return (
              <motion.div
                key={category.title}
                variants={variants.item}
                className={`group relative ${bentoSizes[index]}`}
              >
                <motion.div 
                  className={`relative h-full p-6 bg-gradient-to-br ${isFeatured ? 'from-zinc-900/98 to-black/95' : 'from-zinc-950/90 to-black/85'} border ${isFeatured ? 'border-red-800/70' : 'border-amber-900/40'} overflow-hidden backdrop-blur-sm flex flex-col shadow-lg ${isFeatured ? 'shadow-red-900/30' : 'shadow-black/50'}`}
                  whileHover={{ 
                    borderColor: isFeatured ? 'rgba(153, 27, 27, 1)' : 'rgba(185, 28, 28, 0.8)',
                    boxShadow: isFeatured 
                      ? '0 30px 60px -12px rgba(139, 0, 0, 0.7), inset 0 2px 4px 0 rgba(251, 191, 36, 0.15)'
                      : '0 25px 50px -12px rgba(139, 0, 0, 0.5), inset 0 2px 4px 0 rgba(251, 191, 36, 0.08)'
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Large Kanji watermark - as background overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                    <motion.span 
                      className={`${isFeatured ? 'text-[12rem]' : isLarge ? 'text-[10rem]' : 'text-[8rem]'} font-bold ${isFeatured ? 'text-red-900/20' : 'text-red-900/15'} select-none leading-none`}
                      whileHover={{ 
                        scale: 1.1,
                        rotate: 5,
                        opacity: isFeatured ? 0.3 : 0.25
                      }}
                      transition={{ duration: 0.4 }}
                    >
                      {kanji}
                    </motion.span>
                  </div>

                  {/* Content */}
                  <motion.div 
                    className="relative z-10 flex flex-col h-full justify-between"
                    whileHover={{ x: 2, y: -2 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Title */}
                    <h3 className={`font-bebas ${isFeatured ? 'text-4xl' : isLarge ? 'text-2xl' : 'text-xl'} tracking-wider text-amber-50 mb-2 group-hover:text-amber-100 transition-colors uppercase`}>
                      {category.title}
                    </h3>

                    {/* Description - only for large cards */}
                    {showDescription && (
                      <p className={`${isFeatured ? 'text-base' : 'text-sm'} text-amber-100/60 ${isFeatured ? 'mb-8' : 'mb-4'} leading-relaxed group-hover:text-amber-100/80 transition-colors`}>
                        Advanced expertise across {category.items.length} core technologies and frameworks
                      </p>
                    )}

                    {/* Skills Tags */}
                    <div className="flex flex-wrap gap-2 mt-auto">
                      {category.items.slice(0, maxSkills).map((skill, skillIndex) => (
                        <motion.span
                          key={skill}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={isInView ? { opacity: 1, scale: 1 } : {}}
                          transition={{ duration: 0.3, delay: index * 0.08 + skillIndex * 0.02 }}
                          whileHover={{ 
                            scale: 1.05,
                            backgroundColor: 'rgba(139, 0, 0, 0.4)',
                            borderColor: 'rgba(251, 191, 36, 0.7)',
                            color: 'rgba(251, 191, 36, 1)'
                          }}
                          className={`px-3 py-1.5 ${isFeatured ? 'text-sm' : 'text-xs'} text-amber-100/70 bg-zinc-900/60 border border-zinc-800 rounded-lg group-hover:border-amber-900/40 transition-all duration-300 cursor-default`}
                        >
                          {skill}
                        </motion.span>
                      ))}
                      {category.items.length > maxSkills && (
                        <span className={`px-3 py-1.5 ${isFeatured ? 'text-sm' : 'text-xs'} text-amber-100/50 bg-zinc-900/40 border border-zinc-800 rounded-lg`}>
                          +{category.items.length - maxSkills}
                        </span>
                      )}
                    </div>
                  </motion.div>

                  {/* Hover glow effect */}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-br from-red-900/0 via-red-900/0 to-amber-900/0 opacity-0 group-hover:opacity-100 pointer-events-none"
                    whileHover={{
                      background: isFeatured 
                        ? 'linear-gradient(135deg, rgba(139, 0, 0, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(139, 0, 0, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)'
                    }}
                    transition={{ duration: 0.4 }}
                  />

                  {/* Bottom accent bar */}
                  <motion.div 
                    className={`absolute bottom-0 left-0 right-0 ${isFeatured ? 'h-2' : 'h-1'} bg-gradient-to-r from-red-700 via-amber-600 to-red-700 opacity-0 group-hover:opacity-100`}
                    initial={{ scaleX: 0 }}
                    whileHover={{ scaleX: 1 }}
                    transition={{ duration: 0.5 }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};

export default Skills;
